import { appLocalStorage } from '../utils/storage';
import { notificationRequest } from './api';

export const supportsWebPush = () => Boolean(window.isSecureContext && window.Notification
  && window.PushManager && navigator.serviceWorker);
const installRequired = () => !supportsWebPush() && (/iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));

const deadline = (promise, ms = 15000) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error('PUSH_REGISTRATION_FAILED')), ms);
  Promise.resolve(promise).then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
});

export function createWebPush({ request = notificationRequest, storage = appLocalStorage,
  supported = supportsWebPush, notifications = window.Notification, workers = navigator.serviceWorker,
  onState = () => {}, onReceived = () => {}, onAction = () => {} } = {}) {
  let session = null, initialized = false, generation = 0, disposed = false, publicKey = null, pending = null;
  let state = { platform: 'web', permission: notifications?.permission || 'unsupported', registration: 'unregistered' };
  const publish = patch => { if (!disposed) { state = { ...state, ...patch }; onState(state); } };
  const check = version => { if (disposed || version !== generation || !session) throw new Error('PUSH_SESSION_CHANGED'); };
  const installation = () => {
    let id = storage.getItem('web-push-installation-id');
    if (!id) { id = crypto.randomUUID(); storage.setItem('web-push-installation-id', id); }
    return id;
  };
  const workerReady = () => deadline(workers.ready);
  const nextVersion = () => {
    const version = (Number(storage.getItem('web-push-binding-version')) || 0) + 1;
    if (!Number.isSafeInteger(version) || version > 2147483647) throw new Error('PUSH_REGISTRATION_FAILED');
    storage.setItem('web-push-binding-version', String(version));
    return version;
  };
  const bindWorker = async (registration, userId, bindingVersion = nextVersion(), version = generation, retried = false) => {
    if (!registration.active) throw new Error('PUSH_REGISTRATION_FAILED');
    const channel = new MessageChannel();
    try {
      const response = await deadline(new Promise(resolve => {
        channel.port1.onmessage = event => resolve(event.data);
        registration.active.postMessage({ type: 'wiw:push:owner', userId, bindingVersion }, [channel.port2]);
      }), 5000);
      if (disposed || version !== generation) throw new Error('PUSH_SESSION_CHANGED');
      if (response?.ok) return bindingVersion;
      // Clearing localStorage need not clear IndexedDB. Recover the monotonic
      // counter from the worker, without replaying an obsolete account change.
      if (!retried && Number.isSafeInteger(response?.bindingVersion) && response.bindingVersion >= bindingVersion
        && response.bindingVersion < 2147483647) {
        storage.setItem('web-push-binding-version', String(Math.max(response.bindingVersion,
          Number(storage.getItem('web-push-binding-version')) || 0)));
        return bindWorker(registration, userId, nextVersion(), version, true);
      }
      throw new Error('PUSH_REGISTRATION_FAILED');
    } finally { channel.port1.close(); channel.port2.close(); }
  };
  const onMessage = event => {
    if (!session || disposed || Number(event.data?.recipient_id) !== Number(session.userId)) return;
    if (event.data.type === 'wiw:push:received') onReceived();
    if (event.data.type === 'wiw:push:action') {
      const id = Number(event.data.notification_id);
      if (Number.isSafeInteger(id) && id > 0) {
        onAction({ id, userId: Number(session.userId) });
        event.ports?.[0]?.postMessage({ ok: true });
      }
    }
  };
  workers?.addEventListener('message', onMessage);

  function register(prompt = false) {
    if (disposed || !session) return prompt ? Promise.reject(new Error('PUSH_SESSION_CHANGED')) : Promise.resolve();
    if (!supported() || !publicKey) return prompt ? Promise.reject(new Error('PUSH_REGISTRATION_UNAVAILABLE')) : Promise.resolve();
    // A passive foreground check must never consume the next explicit click
    // before the browser has received a requestPermission user gesture.
    if (!prompt && notifications.permission !== 'granted') {
      publish({ permission: notifications.permission, registration: 'unregistered' });
      return Promise.resolve();
    }
    if (pending?.expiresAt <= Date.now()) { generation++; pending = null; }
    if (pending) return pending.promise;
    const version = generation, owner = session;
    // Permission must be requested in the original click, before any await,
    // network call or service-worker readiness wait (especially in Safari).
    let permission;
    try { permission = prompt && notifications.permission === 'default'
      ? notifications.requestPermission() : Promise.resolve(notifications.permission); }
    catch (error) { publish({ registration: 'error' }); return Promise.reject(error); }
    const work = (async () => {
      // Keep the synchronous user gesture above, but bound the reply just as
      // on Android/iOS. Otherwise settings retains this promise indefinitely
      // when a browser permission callback is lost during suspension.
      const result = await deadline(permission, 60000); check(version);
      publish({ permission: result });
      if (result !== 'granted') {
        publish({ registration: 'unregistered' });
        if (prompt) throw new Error('PUSH_PERMISSION_DENIED');
        return;
      }
      publish({ registration: 'registering' });
      const registration = await workerReady(); check(version);
      let subscription = await deadline(registration.pushManager.getSubscription()); check(version);
      const key = Uint8Array.from(atob(publicKey.replace(/-/g, '+').replace(/_/g, '/')), char => char.charCodeAt(0));
      if (subscription?.options?.applicationServerKey
        && String(new Uint8Array(subscription.options.applicationServerKey)) !== String(key)) {
        await deadline(subscription.unsubscribe()); check(version); subscription = null;
      }
      if (!subscription) subscription = await deadline(registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key }));
      check(version);
      const bindingVersion = await bindWorker(registration, owner.userId, nextVersion(), version); check(version);
      await deadline(request('/devices', { method: 'POST', token: owner.token, body: {
        installation_id: installation(), binding_version: bindingVersion, platform: 'web', environment: 'webpush',
        subscription: subscription.toJSON(),
      } }));
      check(version); publish({ registration: 'registered' });
    })();
    const attempt = { promise: work, expiresAt: Date.now() + 30000 }; pending = attempt;
    work.then(() => { if (pending === attempt) pending = null; }, error => {
      if (pending === attempt) pending = null;
      if (!disposed && version === generation && !['PUSH_SESSION_CHANGED', 'PUSH_PERMISSION_DENIED'].includes(error.message)) publish({ registration: 'error' });
    });
    return work;
  }
  return {
    configure(key) {
      if (key === publicKey) return;
      publicKey = key; generation++; pending = null;
      publish({ registration: 'unregistered' });
      // A capabilities refresh may rotate the server key after the foreground
      // registration has already started with the previous key.
      if (session && !disposed) void register(false).catch(() => {});
    },
    async setSession(next) {
      if (initialized && next?.token === session?.token && next?.userId === session?.userId) {
        if (next) await register(false);
        return;
      }
      const first = !initialized, previous = session, version = ++generation;
      initialized = true; session = next; pending = null;
      publish({ registration: 'unregistered' });
      if (supported() && (first || previous?.userId !== next?.userId)) {
        // Persist no token or private content in the worker. Clear the previous
        // recipient before allowing a late push to appear on a shared device.
        try { const registration = await workerReady(); if (version === generation) await bindWorker(registration, next?.userId || null, nextVersion(), version); } catch { /* Retry at registration. */ }
        if (previous && storage.getItem('web-push-installation-id')) {
          await request('/devices', { method: 'DELETE', token: previous.token,
            body: { installation_id: storage.getItem('web-push-installation-id') } }).catch(() => {});
        }
      }
      if (next && version === generation && !disposed) await register(false);
    },
    register,
    async refreshStatus() {
      publish({ permission: notifications?.permission || 'unsupported',
        installRequired: installRequired(),
        ...(notifications?.permission !== 'granted' ? { registration: 'unregistered' } : {}) });
    },
    async openSettings() { throw new Error('PUSH_PERMISSION_DENIED'); },
    async dispose() { disposed = true; generation++; session = null; workers?.removeEventListener('message', onMessage); },
  };
}
